import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, ChevronRight } from 'lucide-react';
import { Container, Button } from '../components/ui';
import { ResearchInPracticeSection } from '../components/sections';

/*
 * IstaLabs
 * --------
 * Landing page for /istalabs, reached from the sidebar drawer's
 * "Our Innovation / Research — IstaLabs" mega-panel. Three zones:
 *
 *   1. Cover-video hero (`istalabs_cover.webm`) with an overlay card
 *      that greets the visitor with the IstaLabs positioning line
 *      and a CTA. Same treatment as Investors.tsx + ServiceDetail.tsx
 *      so all the "chapter" pages of the site read as one family.
 *
 *   2. "Why we keep innovating" — a trust-building section aimed at
 *      prospective clients. Deliberately human-voiced (no "leverage
 *      / empower / cutting-edge" fluff) and grounded in the honest
 *      pitch: we run internal R&D so your project benefits from work
 *      already done, not so we can invoice a learning curve.
 *
 *   3. "IstaLabs — Ideate. Innovate. Incubate." — split layout with
 *      the innovation-philosophy paragraphs on the left and the
 *      `istalabs_Ideate_Innovate_Incubate.png` infographic on the
 *      right. The infographic already conveys the four pillars
 *      (Ideas that Matter, Applied Research, Build Innovative
 *      Products, AI Labs for What's Next), so we don't duplicate
 *      those as separate blocks below.
 */

// ---------------------------------------------------------------------------
// Media
// ---------------------------------------------------------------------------

const HERO_MEDIA = 'https://istatec-assets.istatec.com/istalabs_cover.webm';
const ISTALABS_LOGO = 'https://istatec-assets.istatec.com/istalabs_logo.png';
const WHY_BUILD_BANNER =
  'https://istatec-assets.istatec.com/istalabs_banner.png';
const IDEATE_IMAGE =
  'https://istatec-assets.istatec.com/istalabs_Ideate_Innovate_Incubate.png';

// Header height offset — matches the fixed site header (h-20).
const HEADER_OFFSET_PX = 80;

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export const IstaLabs: React.FC = () => (
  <>
    {/* ═════════════════ Breadcrumbs (sticky) ═════════════════
        Same pattern as ServiceDetail / Services / Investors — a
        spacer for the fixed header, then a sticky bar that
        re-latches directly under the header once the user starts
        scrolling. */}
    <div style={{ height: `${HEADER_OFFSET_PX}px` }} aria-hidden />
    <div className="sticky top-20 z-40 bg-white border-b border-navy-100 shadow-sm">
      <Container className="py-4">
        <Breadcrumbs />
      </Container>
    </div>

    <IstaLabsHero />
    <WhyWeInnovateSection />
    <IdeateInnovateIncubateSection />
    {/* "Research in practice" — data-driven strip sourced from the
        `in_parctice_research_and_documentsion` Mongo collection.
        This is the natural home for it: after the visitor has read
        WHY we innovate and HOW we operate the practice, this strip
        is the receipts — the actual pipelines, docs, and production
        expressions we've built. Self-hides when the API returns
        an empty list, so a Mongo hiccup leaves no gap on the page. */}
    <ResearchInPracticeSection />
    {/* Closing pitch above the global <ConnectSection/> — the four
        pillars of how our innovation practice actually shows up for
        clients. Full-bleed navy strip so it visually rhymes with
        the connect form immediately below. */}
    <InnovationInActionSection />
  </>
);

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const Breadcrumbs: React.FC = () => (
  <nav
    aria-label="Breadcrumb"
    className="flex flex-wrap items-center gap-2 text-sm"
  >
    <Link
      to="/"
      className="text-navy-500 hover:text-accent-600 transition-colors"
    >
      Home
    </Link>
    <ChevronRight className="w-3.5 h-3.5 text-navy-400 shrink-0" aria-hidden />
    <span aria-current="page" className="font-semibold text-navy-900">
      IstaLabs
    </span>
  </nav>
);

/**
 * Cover video hero. Uses the same responsive height math
 * (`calc(56.25vw - 200px)`, min-h 340) as the service pages so the
 * site's "chapter" pages share one visual family. The video
 * autoplays muted and loops; a mid-grey overlay keeps the white
 * card readable regardless of which frame is on screen at any
 * given moment.
 */
const IstaLabsHero: React.FC = () => {
  const videoRef = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = true;
    const p = v.play();
    // Some browsers freeze on the first frame even with `autoPlay
    // muted` — nudge them once so the video actually rolls.
    if (p && typeof p.catch === 'function') p.catch(() => {});
  }, []);

  return (
    <section className="relative bg-navy-950">
      <div
        className="responsive-hero relative w-full overflow-hidden"
      >
        <video
          ref={videoRef}
          src={HERO_MEDIA}
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div aria-hidden className="absolute inset-0 bg-black/35" />

        <div className="relative flex items-center justify-center px-4 sm:px-6 py-10 sm:py-14 lg:py-20">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className={
              // `text-center` centers all inline / block-with-mx-auto
              // children so the whole card (logo → tagline → button)
              // reads as a single centered stack, per client
              // direction.
              'w-full max-w-3xl md:max-w-4xl lg:max-w-5xl xl:max-w-6xl ' +
              'bg-white rounded-2xl border border-navy-100 ' +
              'shadow-[0_25px_70px_-15px_rgba(2,8,20,0.6)] ' +
              'p-5 sm:p-8 md:p-10 lg:p-12 text-center'
            }
          >
            {/* Wordmark — replaces the text `<h1>IstaLabs</h1>`.
                Uses the client-supplied istalabs_logo.png (black on
                transparent, contains the "Idea • Research • Product
                • AI Labs" tagline built in). Sized in `h-*` so its
                natural aspect ratio is preserved as it scales; the
                `mx-auto` keeps it centered inside the card. `role`
                + `aria-label` give screen readers the same semantic
                as the original H1 the image is replacing. */}
            <img
              src={ISTALABS_LOGO}
              alt="IstaLabs — Idea, Research, Product, AI Labs"
              role="heading"
              aria-level={1}
              className="mx-auto w-auto h-16 sm:h-20 md:h-24 lg:h-32 xl:h-36 mb-6 md:mb-8"
            />
            {/* Tagline sits below the wordmark, centered with a
                comfortable max-width so it doesn't stretch across
                a very wide card. */}
            <p className="max-w-2xl mx-auto text-base sm:text-lg md:text-xl lg:text-[22px] text-navy-700 leading-relaxed mb-7 md:mb-9">
              Istatec&rsquo;s innovation lab where ideas take shape and scale.
            </p>
            {/* Button in a flex row so `justify-center` centers it
                horizontally while its own natural width is preserved
                (an inline-flex Button can't be centered by a
                parent's text-center alone). */}
            <div className="flex justify-center">
              <Link to="/contact">
                <Button
                  variant="primary"
                  size="lg"
                  rightIcon={<ArrowRight className="w-5 h-5" />}
                >
                  Talk to our team
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

/**
 * "Expertise comes from practice, not slides." — the trust-building
 * section aimed at prospective clients. Answers the implicit
 * question: *why should I care that you run an innovation lab?*
 *
 * Layout:
 *   - Full-bleed section (no Container) so content stretches
 *     edge-to-edge, with responsive horizontal padding.
 *   - Two-column grid on md+: text on the left (eyebrow, heading,
 *     three paragraphs), banner image on the right.
 *   - Bigger, bolder type than the site's default body: eyebrow at
 *     14/16px accent-blue caps, h2 at 32→52px, paragraphs at
 *     18→22px `font-medium text-navy-950` for high contrast.
 *   - On mobile the columns stack: text first, image below.
 *
 * Animations:
 *   - Text slides in from left (x: -80).
 *   - Image slides in from the FAR left (x: -600), crossing the
 *     section on its way to the right column — per client
 *     direction. `overflow-hidden` on the section prevents that
 *     journey from creating a horizontal scrollbar.
 */
const WhyWeInnovateSection: React.FC = () => (
  <section className="bg-white py-16 md:py-24 lg:py-28 overflow-hidden">
    <div className="site-wide-container">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        {/* ────────── Left: heading + paragraphs ────────── */}
        <motion.div
          className="order-2 lg:order-1 max-w-2xl"
          initial={{ opacity: 0, x: -80 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        >
          <p className="text-sm md:text-base font-semibold uppercase tracking-[0.12em] text-accent-600 mb-5 md:mb-6">
            Why we keep building
          </p>
          <h2 className="text-3xl sm:text-4xl md:text-4xl lg:text-[52px] font-bold text-navy-950 leading-[1.16] mb-6 md:mb-8">
            Expertise comes from practice, not slides.
          </h2>
          <div className="space-y-5 md:space-y-6 text-lg md:text-xl lg:text-xl font-normal text-navy-950 leading-relaxed">
            <p>
              Innovation at Istatec isn&rsquo;t a slide in a deck — it&rsquo;s
              a working discipline. Every quarter our team invests in
              internal projects that stretch beyond current engagements:
              prototyping new AI models, building reference architectures
              for emerging cloud patterns, and stress-testing frameworks
              before they show up in mainstream stacks.
            </p>
            <p>
              When a client brings us a problem that hasn&rsquo;t been
              solved commercially yet, we&rsquo;ve often already built
              something close to it in-house. That&rsquo;s the deal:{' '}
              <span className="font-bold">
                you get a team with proven expertise on what&rsquo;s next,
                not one guessing confidently about it.
              </span>{' '}
              Your project benefits from the R&amp;D we&rsquo;ve already
              done — so you&rsquo;re not funding our learning curve, and
              you&rsquo;re not the pilot for a technology we haven&rsquo;t
              validated ourselves.
            </p>
            <p>
              We build our own AI platforms, evaluation harnesses, and
              reference architectures for one simple reason: we want to
              hand your team the tools we&rsquo;ve already proven work —
              not experiment on your production system. That&rsquo;s how
              we stay useful to the clients who trust us, and how we stay
              ahead of what the market will need next.
            </p>
          </div>
        </motion.div>

        {/* ────────── Right: banner image ──────────
            Slides in from far-left (x: -600), crossing the section on
            its way to the right column. `overflow-hidden` on the
            outer section clips that off-screen journey. */}
        <motion.div
          className="order-1 lg:order-2 relative w-full"
          initial={{ opacity: 0, x: -600 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        >
          <img
            src={WHY_BUILD_BANNER}
            alt="IstaLabs — Ideas, Research, Products, AI Labs signage"
            className="w-full h-auto rounded-2xl shadow-[0_25px_60px_-15px_rgba(2,8,20,0.35)]"
            loading="lazy"
          />
        </motion.div>
      </div>
    </div>
  </section>
);

/**
 * "IstaLabs — Ideate. Innovate. Incubate." — full-bleed split layout
 * with the philosophy narrative on the left and the four-pillar
 * IstaLabs infographic on the right.
 *
 * Animations:
 *   - Section heading fades in from below.
 *   - Text column slides in from the far left (x: -80).
 *   - Image slides in from the FAR right (x: +600), moving inward
 *     to its column on the right — per client direction. `overflow-
 *     hidden` on the outer section clips that off-screen journey.
 *   - On mobile the columns stack: image first, text beneath it.
 *
 * Typography is deliberately larger + bolder than site-default body
 * copy so the section reads as prominent and premium (client asked
 * for higher-contrast, easier-to-read text).
 */
const IdeateInnovateIncubateSection: React.FC = () => (
  <section className="bg-navy-50/60 py-16 md:py-24 lg:py-28 overflow-hidden">
    <div className="site-wide-container">
      <motion.h2
        className="text-3xl sm:text-4xl md:text-4xl lg:text-[52px] font-bold text-navy-950 leading-[1.16] mb-10 md:mb-14"
        initial={{ opacity: 0, y: 12 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-80px' }}
        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
      >
        IstaLabs — Ideate. Innovate. Incubate.
      </motion.h2>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        {/* ────────── Left: philosophy paragraphs ────────── */}
        <motion.div
          className="space-y-5 md:space-y-6 text-lg md:text-xl lg:text-xl font-normal text-navy-950 leading-relaxed order-2 lg:order-1 max-w-2xl"
          initial={{ opacity: 0, x: -80 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        >
          <p>
            Rooted in our people-first, ship-what-matters philosophy, we
            fuse creativity with technology to build intelligent,
            user-centric solutions. IstaLabs incubates centers of
            excellence focused on research and development of next-gen
            technologies — agentic automation, cognitive intelligence,
            applied AI, and platform engineering.
          </p>
          <p>
            It builds and nurtures an innovative ecosystem of partners:
            startups we co-build with, academic collaborators we learn
            from, and the vendors whose tooling we push to its limits
            before recommending it to you.
          </p>
          <p>
            IstaLabs also offers{' '}
            <span className="font-bold">innovation-as-a-service</span> for
            enterprise clients — helping you co-innovate and prototype
            new solutions by contextualising emerging technologies to
            your business. We bring design thinking, domain expertise,
            and the full engineering capacity of Istatec&rsquo;s team
            into short, focused engagements that go from idea to working
            prototype in weeks.
          </p>
        </motion.div>

        {/* ────────── Right: four-pillar infographic ──────────
            Slides in from the far right (x: +600) so it appears to
            arrive from off-screen right — per client direction. */}
        <motion.div
          className="order-1 lg:order-2 relative w-full"
          initial={{ opacity: 0, x: 600 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        >
          <img
            src={IDEATE_IMAGE}
            alt="IstaLabs — Ideas that Matter, Applied Research, Build Innovative Products, AI Labs for What's Next"
            className="w-full h-auto rounded-2xl shadow-[0_25px_60px_-15px_rgba(2,8,20,0.35)]"
            loading="lazy"
          />
        </motion.div>
      </div>
    </div>
  </section>
);

/**
 * "Innovation in Action" — closing pillar section on the IstaLabs
 * page, sitting immediately above the global ConnectSection.
 *
 * Layout mirrors the Zensar reference: full-bleed navy strip with a
 * left-aligned heading and a 4-column grid of pillars below. Each
 * pillar has a bold white title and a comfortable-to-read white
 * body paragraph (font-medium, 90% opacity for that near-white
 * eye-comfort). On smaller viewports the grid collapses to 2 → 1
 * columns.
 *
 * Content is adapted to Istatec's real tech stack (per client
 * direction): AI implementation, automation, Salesforce, data
 * science, cloud, and GenAI — not blockchain / quantum which we
 * don't actually ship for clients.
 */
const INNOVATION_ACTIONS: readonly {
  title: string;
  description: string;
}[] = [
  {
    title: 'Full Lifecycle Partnership',
    description:
      "We collaborate with clients at every stage of their transformation \u2014 from the first scoping call through design-thinking workshops, rapid prototyping, and production rollout. Innovation isn\u2019t just a concept here; it\u2019s a working process. We co-create solutions that align with your strategic goals and deliver measurable impact.",
  },
  {
    title: 'Co-innovation and Emerging Technologies',
    description:
      "We rapidly develop proofs-of-concept using the technologies enterprises actually need today \u2014 AI implementation, workflow automation, Salesforce, data science, and cloud-native platforms. Working closely with your team, we turn bold ideas into working prototypes that can be tested, refined, and scaled, not slide decks that get quietly shelved.",
  },
  {
    title: 'Robust Innovation Ecosystem',
    description:
      "A diverse ecosystem powers our innovation engine. We bring together internal centers of excellence, strategic partners, technology vendors, academic collaborators, and startups so cross-disciplinary work is the default rather than the exception. That network gives us varied expertise to draw on \u2014 the fuel behind breakthroughs that actually ship.",
  },
  {
    title: 'Applied Research',
    description:
      "We invest in high-impact applied research across AI, GenAI, automation, and cross-domain integration \u2014 giving our engineers structured mentorship, editorial guidance, and academic collaboration so their work can be published and patented. The result: research that turns into business value your team can actually use, not shelf-ware papers.",
  },
];

const InnovationInActionSection: React.FC = () => (
  <section
    className="relative overflow-hidden text-white"
    style={{
      // Same deep-navy gradient as the Contact form, the global
      // ConnectSection, and the service sub-offerings section — all
      // the site's dark surfaces share this palette so they feel
      // like a single visual family.
      background:
        'linear-gradient(135deg, #020815 0%, #051124 35%, #0a2148 65%, #071730 100%)',
    }}
  >
    {/* Slowly-drifting orbs for depth. Blue / indigo family so they
        complement the deep navy without a colour clash. */}
    <motion.div
      aria-hidden
      className="pointer-events-none absolute -top-32 -left-24 w-[420px] h-[420px] rounded-full blur-3xl bg-blue-600/20"
      animate={{ x: [0, 60, 0], y: [0, 50, 0] }}
      transition={{ duration: 20, repeat: Infinity, ease: 'easeInOut' }}
    />
    <motion.div
      aria-hidden
      className="pointer-events-none absolute -bottom-36 -right-24 w-[520px] h-[520px] rounded-full blur-3xl bg-indigo-600/20"
      animate={{ x: [0, -60, 0], y: [0, -40, 0] }}
      transition={{ duration: 24, repeat: Infinity, ease: 'easeInOut' }}
    />

    <div className="relative z-10 py-16 md:py-24 lg:py-28 site-wide-container">
      <motion.h2
        className="text-3xl sm:text-4xl md:text-4xl lg:text-[52px] font-bold text-white leading-[1.16] mb-12 md:mb-16"
        initial={{ opacity: 0, y: 12 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-80px' }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      >
        Innovation in Action
      </motion.h2>

      {/* 1 / 2 / 4 columns depending on breakpoint. `items-start`
          keeps each pillar top-aligned so the section reads as a
          row of distinct claims rather than centrally floated
          blocks that shift with the copy length. */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-8 lg:gap-x-10 gap-y-12 md:gap-y-14 items-start">
        {INNOVATION_ACTIONS.map((action, i) => (
          <InnovationPillar
            key={action.title}
            action={action}
            animationDelay={Math.min(i, 3) * 0.08}
          />
        ))}
      </div>
    </div>
  </section>
);

const InnovationPillar: React.FC<{
  action: { title: string; description: string };
  animationDelay: number;
}> = ({ action, animationDelay }) => (
  <motion.div
    initial={{ opacity: 0, y: 16 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: '-40px' }}
    transition={{
      duration: 0.5,
      delay: animationDelay,
      ease: [0.22, 1, 0.36, 1],
    }}
  >
    {/* Thin accent top-rule so each pillar has a visual anchor
        without needing an icon (per site pattern established on
        the sub-offerings section). */}
    <div className="h-px bg-white/25 mb-5" aria-hidden />
    <h3 className="text-xl md:text-2xl font-bold text-white leading-tight mb-4">
      {action.title}
    </h3>
    <p className="text-base md:text-[17px] font-medium text-white/90 leading-relaxed">
      {action.description}
    </p>
  </motion.div>
);
